import { Component, OnInit, AfterViewChecked } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ToasterService } from 'angular2-toaster';
import axios from 'axios';
import { ENDPOINTS } from '../app.config';
import { CompanyService } from '../company.service';
import { UserInfoService } from '../shared/user-info.service';

// Types
import { UserInfo }       from '../shared/types/user-info';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-companies',
  templateUrl: './companies.component.html',
  styleUrls: ['./companies.component.css'],
  // Is already provided in ../app.module.ts
  //  but the UserInfoService, in the constructor, needs access to ActivatedRoute.params
  //  https://stackoverflow.com/questions/39977962/angular-2-0-2-activatedroute-is-empty-in-a-service
  providers: [UserInfoService]
})
export class CompaniesComponent implements OnInit {
  companies: any;
  errors: any;
  currentPage = 1;
  companiesTotal: number;
  companiesPerPage: number;
  totalPages: number;
  gettingCompaniesInProgress: boolean;
  router: Router;
  route : ActivatedRoute;
  einkinformation : string;
  // <string> when it is provided by ActivatedRoute, in case of USER_ROLES.ADMINISTRATOR
  // <number> whet it is provided by the API response, in case of USER_ROLES.COMPANY_ADMIN
  companyOwnerId : string | number;
  companyService: CompanyService;
  eink_support: boolean;
  newCompany: {
    name: string;
    business_owner_id : string,
    eink_support: boolean,
    admin: {
      email: '';
      password: '';
      passwordRepeat: '';
    };

  };
  toaster: ToasterService;
  companyName : string;
  userRole: string;
  businesOwnerName: string;
  businessOwnerIdLocalStorage = localStorage.getItem('businessOwnerId');

  // Messages
  company_successfully_added: string;
  error_accessing_user: string;
  error_loading_companies: string;
  company_successfully_deleted: string;
  error_deleting_the_company: string;
  password_changed_successfully: string;
  password_not_changed: string;

  constructor(
    toaster: ToasterService,
    router: Router,
    route : ActivatedRoute,
    companyService: CompanyService,
    private userInfoService: UserInfoService,
    private translate: TranslateService
  ) {
    this.toaster = toaster;
    this.errors = [];
    this.einkinformation = "";
    this.newCompany = {
      name: '',
      business_owner_id : '',
      eink_support: false,
      admin: {
        email: '',
        password: '',
        passwordRepeat: '' }
    };
    this.router = router;
    this.route = route;
    this.companyService = companyService;
  }

  ngOnInit() {
    this.gettingCompaniesInProgress = false;
    // In case of ADMINISTRATOR, the URI is: "/companies/companyOwnerId" => this.companyOwnerId<string>
    this.companyOwnerId = this.route.snapshot.paramMap.get('companyOwnerId');
    this.getCompanies();
    this.getUserInfo();

    // Messages translations
    this.translate.stream('SHARED.Messages.company_successfully_added').subscribe((res: string) => {
      this.company_successfully_added = res;
    });
    this.translate.stream('SHARED.Messages.error_accessing_user').subscribe((res: string) => {
      this.error_accessing_user = res;
    });
    this.translate.stream('SHARED.Messages.error_loading_companies').subscribe((res: string) => {
      this.error_loading_companies = res;
    });
    this.translate.stream('SHARED.Messages.company_successfully_deleted').subscribe((res: string) => {
      this.company_successfully_deleted = res;
    });
    this.translate.stream('SHARED.Messages.error_deleting_the_company').subscribe((res: string) => {
      this.error_deleting_the_company = res;
    });
    this.translate.stream('SHARED.Messages.password_changed_successfully').subscribe((res: string) => {
      this.password_changed_successfully = res;
    });
    this.translate.stream('SHARED.Messages.password_not_changed').subscribe((res: string) => {
      this.password_not_changed = res;
    });
  }

  doValidation() {
    this.errors = [];
    if (!this.newCompany.name) {
      this.errors.push('name');
    }
    if (!this.newCompany.admin.email) {
      this.errors.push('email');
    }
    if (!this.newCompany.admin.password) {
      this.errors.push('password');
    }
    return this.errors.length === 0;
  }

  addNewCompany() {

    const _self = this;
    const isFormValid = _self.doValidation();
    if (isFormValid) {
      // When ADMINISTRATOR wants to add a company to a particular BUSINESS_OWNER
      // check lines 24 & 25 too see situations when this.companyOwnerId is <string> or <number>
      if( typeof this.companyOwnerId == 'string' ) {
        this.newCompany.business_owner_id = this.companyOwnerId
      }
      axios.post(ENDPOINTS.POST_COMPANY, _self.newCompany).then(
        response => {
          _self.toaster.pop('success', this.company_successfully_added);
          _self.getCompanies();
          _self.newCompany  = {
            name: '',
            business_owner_id : '',
            eink_support: false,
            admin: {
              email: '',
              password: '',
              passwordRepeat: ''}
          };
        },
        error => {
          _self.toaster.pop('error', error.response.data.message);
        },
      );
    }
  }

  onEnter(event) {
    this.addNewCompany();
  }

  goToCompanyDetails(companyId) {
    this.companyService.setCompanyId(companyId);
    this.router.navigate(['company/' + companyId]);
  }

  initPaging(response) {
    const _self = this;
    _self.companiesTotal = parseInt(response.headers['total'], 10);
    _self.companiesPerPage = parseInt(response.headers['per-page'], 10);
    _self.totalPages = Math.floor(
      _self.companiesTotal % _self.companiesPerPage,
    ) === 0
      ? _self.companiesTotal / _self.companiesPerPage
      : Math.floor(_self.companiesTotal / _self.companiesPerPage) + 1;
    // Pagination case
    //   Example: companiesTotal = 11, companiesPerPage = 10, currentPage = 2, only 1 company on the page
    //   After 11 company is deleted, page 2 doesn't exist anymore
    //   Therefore _currentPage needs to be reduced with -1
    if( _self.currentPage > _self.totalPages){
      _self.currentPage--;
    }
  }

  loadDevices(page, isDisabled) {
    if (!isDisabled && !this.gettingCompaniesInProgress) {
      this.currentPage = page;
      this.getCompanies();
    }
  }

  getCompanies() {

    const _self = this;
    if (_self.gettingCompaniesInProgress) {
      return;
    }
    _self.gettingCompaniesInProgress = true;

    // Get owner id
    if( typeof this.companyOwnerId == 'string'){
      getOnwerCompanies(this.companyOwnerId, _self)
    } else {
      // In case of pagination, no need to request the ID from the API
      //  because it is already saved
      if( typeof this.companyOwnerId == 'number'){
          getOnwerCompanies(_self.companyOwnerId, _self);
      }
      // request the ID from the API
      else {
        axios.get(ENDPOINTS.GET_CURRENT_USER).then(
          responseUser => {
            _self.companyOwnerId = responseUser.data.id;
            getOnwerCompanies(_self.companyOwnerId, _self);
          },
          errorGettingUser => {
            _self.toaster.pop('error', this.error_accessing_user);
          },
        );
      }
    }


    // Get owner companies
    function getOnwerCompanies(companyOwnerId, _self){

      axios.get(ENDPOINTS.GET_COMPANIES + "?business_owner_id=" + _self.companyOwnerId).then(
      // axios.get(ENDPOINTS.GET_OWNER_COMPANIES).then(
        response => {
          _self.initPaging(response);

          // Get page specific companies
          axios
            .get(
              ENDPOINTS.GET_COMPANIES + "?business_owner_id=" + _self.companyOwnerId +
                '&page=' +
                _self.currentPage +
                '&per_page=' +
                _self.companiesPerPage,
            )
            .then(responseWithPaging => {

              _self.gettingCompaniesInProgress = false;

              _self.companies = responseWithPaging.data;
              _self.companies.forEach(element => {

                _self.einkinformation += element.id+","+element.eink_support+";";
              });
              localStorage.setItem("einkinfo",_self.einkinformation);

            }, err => {
              _self.gettingCompaniesInProgress = false;
            });
        },
        error => {
          _self.gettingCompaniesInProgress = false;
          _self.toaster.pop('error', this.error_loading_companies);
        },
      );
    } // getOnwerCompanies()

  }

  generatePages() {
    if (!this.totalPages) {
      return [];
    }
    const arr = [];
    for (let i = 0; i < this.totalPages; i++) {
      arr.push(i + 1);
    }
    return arr;
  }

  deleteCompany(companyId) {
    const _self = this;
    axios.delete(ENDPOINTS.DELETE_COMPANY.replace(':id', companyId)).then(
      response => {
        _self.getCompanies();
        _self.toaster.pop('success', this.company_successfully_deleted);
      },
      error => {
        _self.toaster.pop('error', this.error_deleting_the_company);
      },
    );
  }

  updateCompany(company: any) {

    const _self = this;

    const patchCompany = {
      id: company.id,
      name: company.name,
      eink_support: company.eink_support,
      admin: {
        password: null,
        email: null
      }
    };

    if (company.admin.password) {
      patchCompany.admin.password = company.admin.password
      delete patchCompany.admin.email
    } else {
      patchCompany.admin.email = company.admin.email
      delete patchCompany.admin.password
    }
    axios.patch(ENDPOINTS.UPDATE_COMPANY.replace(':id', company.id), patchCompany).then(
      response => {
         this.resetAdminFieldPasswords(company);
         if(patchCompany.admin.password){
           // pasword change was successful
           _self.toaster.pop('success', this.password_changed_successfully);
         }
      },
      error => {
        if(patchCompany.admin.password){
          // pasword change has failed
          _self.toaster.pop('error', this.password_not_changed);
        }  else {
          _self.toaster.pop('error', this.error_deleting_the_company);
        }
      },
    );
  }

  isPasswordSubmitOff(company){
    if(typeof company.admin.password === "string" && company.admin.password.length){
      return company.admin.password !== company.admin.passwordRepeat;
    } else {
      return true;
    }
  }
  resetAdminFieldPasswords(company){
    company.admin.password='';
    company.admin.passwordRepeat='';
  }


  getUserInfo(){
    let _self = this;
    this.userInfoService.getInformations()
    .subscribe( (info : UserInfo) => {
      _self.userRole   = info.userRole;
      _self.companyName = info.companyName;
      _self.businesOwnerName = info.businesOwnerName;
    });
  }

}



// WEBPACK FOOTER //
// /Users/mihagresak/Documents/git-projects/vintegras-angular-new-clean/src/app/companies/companies.component.ts